import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Button,
  Paper,
  Switch as MaterialSwitch,
  Theme,
  Tooltip,
} from '@mui/material';
import { AlertColor as AlertSeverity } from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridRenderCellParams,
  GridRowData,
  GridSortModel,
} from '@mui/x-data-grid';
import { AxiosError, AxiosResponse, Method } from 'axios';
import { ValidationError } from 'class-validator';
import moment from 'moment';
import { OptionsObject, SnackbarKey } from 'notistack';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, UseMutationResult } from 'react-query';
import {
  generatePath,
  Link,
  Route,
  Switch,
  useHistory,
} from 'react-router-dom';
import {
  SetterOrUpdater,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';

import { Logger } from '../../common/Logger';
import { MouseOverPopover } from '../../common/MouseOverPopover';
import { trackAddCadence } from '../../common/userTracking';
import { Cadence } from '../../lib/kixie-entities/tables/Cadence';
import { ViewCadenceReport } from '../../lib/kixie-entities/views/ViewCadenceReport';
import { IUserAtom, Role, user } from '../../recoil/atoms/user';
import { refetchToggle } from '../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../services/rest/makeAPIRequest';
import { toAbsoluteUrl } from '../../_metronic/_helpers';
import { CadenceModal } from '../components/modals/CadenceModal';
import { DeleteCadenceModal } from '../components/modals/DeleteCadenceModal';
import { DeleteCadenceScheduledModal } from '../components/modals/DeleteCadenceScheduledModal';
import {
  IAlertWithProgressBar,
  useSnackbarWithStyles,
} from '../components/notifications/SnackbarProviderWithStyles';
import { ActionColumn } from '../modules/Manage/pages/queues-table/ActionColumns';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
  IToolbarComponent,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

// const options = [
//   'Create a merge commit',
//   '\xa0\xa0 Edit \xa0',
//   'Rebase and merge',
// ];

// const SplitButton = ({ params }: { params: GridValueFormatterParams }) => {
//   const [open, setOpen] = React.useState(false);
//   const anchorRef = React.useRef<HTMLDivElement>(null);
//   const cadenceId = params.row.id;

//   const [selectedIndex, setSelectedIndex] = React.useState(1);

//   const handleMenuItemClick = (
//     event: React.MouseEvent<HTMLLIElement, MouseEvent>,
//     index: number
//   ) => {
//     setSelectedIndex(index);
//     setOpen(false);
//   };

//   const handleToggle = () => {
//     setOpen((prevOpen) => !prevOpen);
//   };

//   const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
//     if (
//       anchorRef.current &&
//       anchorRef.current.contains(event.target as HTMLElement)
//     ) {
//       return;
//     }

//     setOpen(false);
//   };

//   return (
//     <Grid container direction="column" alignItems="flex-end">
//       <Grid item xs={12}>
//         <ButtonGroup
//           variant="contained"
//           color="primary"
//           ref={anchorRef}
//           aria-label="split button"
//         >
//           <Button component={Link} to={`/manage/cadences/${cadenceId}/edit`}>
//             {options[selectedIndex]}
//           </Button>
//           <Button
//             color="primary"
//             size="small"
//             aria-controls={open ? 'split-button-menu' : undefined}
//             aria-expanded={open ? 'true' : undefined}
//             aria-label="select merge strategy"
//             aria-haspopup="menu"
//             onClick={handleToggle}
//           >
//             <ArrowDropDownIcon />
//           </Button>
//         </ButtonGroup>
//         <Popper
//           open={open}
//           anchorEl={anchorRef.current}
//           role={undefined}
//           transition
//           disablePortal
//           style={{ zIndex: 2 }}
//         >
//           {({ TransitionProps, placement }) => (
//             <Grow
//               {...TransitionProps}
//               style={{
//                 transformOrigin:
//                   placement === 'bottom' ? 'center top' : 'center bottom',
//               }}
//             >
//               <Paper>
//                 <ClickAwayListener onClickAway={handleClose}>
//                   <MenuList id="split-button-menu">
//                     {options.map((option, index) => (
//                       <MenuItem
//                         key={option}
//                         disabled={index === 2}
//                         selected={index === selectedIndex}
//                         onClick={(event) => handleMenuItemClick(event, index)}
//                       >
//                         {option}
//                       </MenuItem>
//                     ))}
//                   </MenuList>
//                 </ClickAwayListener>
//               </Paper>
//             </Grow>
//           )}
//         </Popper>
//       </Grid>
//     </Grid>
//   );
// };

// TODO: move to centeralized location
export enum ErrorEnum {
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CADENCE_INVALID = 'CADENCE_INVALID',
  CADENCE_ACTION_REQUIRED = 'CADENCE_ACTION_REQUIRED',
  CADENCE_IS_FORWARDED_TO = 'CADENCE_IS_FORWARDED_TO',
  CADENCE_NOT_FOUND = 'CADENCE_NOT_FOUND',
  FK_CADENCE_ACTION__SMS_TEMPLATES = 'FK_CADENCE_ACTION__SMS_TEMPLATES',
  FK_CADENCE__QUEUES = 'FK_CADENCE__QUEUES',
  FK_CADENCE__BUSINESS_DISPOSITIONS = 'FK_CADENCE__BUSINESS_DISPOSITIONS',
}

/**
 * Custom error containing ValidationError[]
 *
 * @see https://javascript.info/custom-errors
 */
export class ErrorOnValidate<T = any> extends Error {
  errorType?: ErrorEnum;
  validationErrors?: ValidationError[];
  entities?: T[];
  isErrorOnValidate: boolean;

  constructor(
    message: string,
    errorType?: ErrorEnum,
    errorData?: {
      validationErrors?: ValidationError[];
      entities?: T[];
    }
  ) {
    super(message); // (1)
    this.name = 'ErrorOnValidate'; // (2)
    this.isErrorOnValidate = true;
    this.errorType = errorType;
    this.validationErrors = errorData?.validationErrors;
    this.entities = errorData?.entities;
  }
}

const CadenceToggle = (props: {
  params: GridRenderCellParams;
  mutation: UseMutationResult<
    AxiosResponse<any>,
    unknown,
    {
      endpoint: string;
      payload?:
        | {
            [key: string]: any;
          }
        | undefined;
      method?: Method | undefined;
      omitBusinessIdAndApikey?: boolean | undefined;
    },
    unknown
  >;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey;
  setRefetchToggleForCadences: SetterOrUpdater<any>;
}) => {
  const [checked, setChecked] = useState<boolean>(Boolean(props.params.value)); // [cadenceId]: [boolean]

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ): Promise<void> => {
    setChecked(checked);
    const cadenceId = props.params.row.id;
    try {
      await props.mutation.mutateAsync({
        endpoint: `${endpointEnum.PATCH_CADENCE}/${cadenceId}`,
        payload: {
          status: checked ? 'active' : 'inactive',
        } as Partial<Cadence>,
        method: 'patch',
        omitBusinessIdAndApikey: true,
      });

      // refetch business queues
      props.setRefetchToggleForCadences({});
    } catch (exception) {
      const axiosError = exception as AxiosError;
      const errorOnValidate = axiosError?.response?.data as ErrorOnValidate;

      setChecked(!checked);

      let message: string | React.ReactNode =
        'Failed to change status of cadence';
      let severity: AlertSeverity = 'error';

      if (
        errorOnValidate?.errorType === 'CADENCE_IS_FORWARDED_TO' &&
        errorOnValidate?.entities
      ) {
        const cadences: Cadence[] = errorOnValidate.entities;
        message = (
          <div>
            Cannot inactivate cadence since it's used in the following cadences:
            <ul>
              {cadences.map((cadence) => (
                <li key={cadence.cadenceId}>
                  <a
                    href={`/manage/cadences/${cadence.cadenceId}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'white',
                      textDecoration: 'underline',
                    }}
                  >
                    {cadence.cadenceName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );
        severity = 'warning';
      }

      props.enqueueSnackbarWithStyles({
        message,
        alertProps: {
          severity,
        },
      });
    }
  };

  return (
    <MaterialSwitch
      checked={checked}
      onChange={handleChange}
      color="primary"
      name="cadenceEnabled"
      inputProps={{ 'aria-label': 'primary checkbox' }}
    />
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  dataTable: {
    '& .MuiDataGrid-colCellTitle': {
      whiteSpace: 'normal',
    },
    '& .MuiDataGrid-columnsContainer': {
      maxHeight: 'none !important',
      lineHeight: '26px !important',
    },
  },
  toolbarButton: {
    marginTop: '20px',
    marginBottom: '25px',
  },
  button: {
    marginTop: '30px',
    height: '50px',
    marginRight: theme.spacing(1),
  },
  disabled:
    theme.palette.mode === 'dark'
      ? {
          backgroundColor: 'grey',
          '&:hover': {
            backgroundColor: 'darkgrey',
          },
        }
      : {
          backgroundColor: 'lightgrey',
          '&:hover': {
            backgroundColor: 'grey',
          },
        },
}));

export function ManageCadences2() {
  const _classes = useStyles();
  const history = useHistory();
  const [deleteItemId, setDeleteItemId] = useState<
    string | number | undefined
  >();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtom = userAtomLoadable.contents as IUserAtom;
  const cadenceReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtom.businessid}`,
      method: 'GET',
    })
  );

  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));

  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  const cadenceReportCache = useRef<ViewCadenceReport[] & GridRowData[]>([]);

  cadenceReportCache.current = useMemo(
    () =>
      (cadenceReportLoadable.contents?.response as AxiosResponse)?.data ??
      cadenceReportCache.current,
    [cadenceReportLoadable]
  );
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { canManage, role } = permissionsLoadable.contents as IPermissions;
  const isUser = role === Role.USER;
  const ToolbarComponent = useCallback(
    (props: IToolbarComponent) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Cadences"
        // numSelected={selected.length}
        rightComponent={
          <ToolbarAddDeleteButton
            className="newCadenceAdd"
            addButtonText="New Cadence"
            // numSelected={selected.length}
            onClickForAdd={() => {
              trackAddCadence();
              history.push('/manage/cadences/add');
            }}
            // onClickForDelete={() => {
            //   history.push('/manage/queues/deleteQueues');
            // }}
            isLoading={props.isLoading}
          />
        }
      />
    ),
    []
  );
  const setRefetchToggleForCadences = useSetRecoilState(
    refetchToggle(
      `${endpointEnum.GET_CADENCE_REPORT}?filter=businessId:${userAtom.businessid}`
    )
  );
  const handlePreflightCheck = async (id: string | number) => {
    let message: string | React.ReactNode = 'Update sucessful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success';
    try {
      const response = await mutation.mutateAsync({
        // placeholder for actual call
        endpoint: `${endpointEnum.DELETE_CADENCE}/${id}?preflight=1`,
        method: 'delete',
        omitBusinessIdAndApikey: true,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'auto',
      });
      if (response.status === 200) {
        history.push(generatePath('/manage/cadences/:id/delete', { id }));
      }
    } catch (exception) {
      const axiosError = exception as AxiosError;
      const errorOnValidate = axiosError?.response?.data as ErrorOnValidate;
      if (
        errorOnValidate?.errorType === 'CADENCE_IS_FORWARDED_TO' &&
        errorOnValidate?.entities
      ) {
        const cadences: Cadence[] = errorOnValidate.entities;
        message = (
          <div>
            Cannot remove cadence since it's used in the following cadences:
            <ul>
              {cadences.map((cadence) => (
                <li key={cadence.cadenceId}>
                  <a
                    href={`/manage/cadences/${cadence.cadenceId}/edit`}
                    rel="noopener noreferrer"
                    style={{
                      color: 'white',
                      textDecoration: 'underline',
                    }}
                  >
                    {cadence.cadenceName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );
        severity = 'warning';
      } else {
        message = 'Failed to delete cadence (please try again later)';
        severity = 'error';
      }
      enqueueSnackbarWithStyles({
        message,
        alertProps: {
          severity,
        },
      });
    }
  };
  const handleDeletion = async (id: string | number) => {
    // const { response, exception, status } = await phpRequest({
    //   methodName: methodNameEnum.DELETE_QUEUE,
    //   payload: {
    //     queueid: id,
    //   },
    // });
    let message: string | React.ReactNode = 'Update sucessful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success';

    try {
      const response = await mutation.mutateAsync({
        // placeholder for actual call
        endpoint: `${endpointEnum.DELETE_CADENCE}/${id}`,
        method: 'delete',
        omitBusinessIdAndApikey: true,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'auto',
      });

      // log success
      Logger.trace(
        {
          message,
          response,
        },
        'ManageCadences2:handleDeletion'
      );

      // refetch business queues
      setRefetchToggleForCadences({});
    } catch (exception) {
      const axiosError = exception as AxiosError;
      const errorOnValidate = axiosError?.response?.data as ErrorOnValidate;
      if (
        errorOnValidate?.errorType === 'CADENCE_IS_FORWARDED_TO' &&
        errorOnValidate?.entities
      ) {
        const cadences: Cadence[] = errorOnValidate.entities;
        message = (
          <div>
            Cannot remove cadence since it's used in the following cadences:
            <ul>
              {cadences.map((cadence) => (
                <li key={cadence.cadenceId}>
                  <a
                    href={`/manage/cadences/${cadence.cadenceId}/edit`}
                    rel="noopener noreferrer"
                    style={{
                      color: 'white',
                      textDecoration: 'underline',
                    }}
                  >
                    {cadence.cadenceName}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        );
        severity = 'warning';
      } else {
        message = 'Failed to delete cadence (please try again later)';
        severity = 'error';
      }

      // log success
      Logger.trace(
        {
          message,
          exception,
        },
        'ManageCadences2:handleDeletion'
      );
    }

    // clear selections list
    setDeleteItemId(undefined);

    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
  };

  const handleScheduledDeletion = async (id: string | number) => {
    let message: string | React.ReactNode = 'Update sucessful'; // ! to ignore usage before assignment error
    let severity: AlertSeverity = 'success';

    try {
      const response = await mutation.mutateAsync({
        // placeholder for actual call
        endpoint: `${endpointEnum.DELETE_SCHEDULED_CADENCE}/${id}`,
        method: 'delete',
        omitBusinessIdAndApikey: true,
        domain: process.env.VITE_APP_CDK_DOMAIN,
        stage: 'auto',
      });

      // log success
      Logger.trace(
        {
          message,
          response,
        },
        'ManageCadences2:handleDeletion'
      );

      // refetch business queues
      setRefetchToggleForCadences({});
    } catch (exception) {
      message = 'Failed to delete cadence (please try again later)';
      severity = 'error';

      // log success
      Logger.trace(
        {
          message,
          exception,
        },
        'ManageCadences2:handleDeletion'
      );
    }

    // clear selections list
    setDeleteItemId(undefined);

    enqueueSnackbarWithStyles({
      message,
      alertProps: {
        severity,
      },
    });
  };

  const handleScheduledDeletionDialog = async (id: string | number) => {
    history.push(
      generatePath('/manage/cadences/cadenceScheduledActions/:id/delete', {
        id,
      })
    );
  };

  const columns = [
    {
      field: 'cadenceId',
      headerName: 'ID',
      hide: isUser,
      width: 80,
    },
    {
      field: 'cadenceIsActive',
      headerName: 'Active',
      renderCell: (params: GridRenderCellParams) => (
        <CadenceToggle
          params={params}
          mutation={mutation}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          setRefetchToggleForCadences={setRefetchToggleForCadences}
        />
      ),
      disableExport: true,
    },
    {
      field: 'cadenceName',
      headerName: 'Cadence',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title="Edit">
            <Link to={`/manage/cadences/${params.row.cadenceId}/edit`}>
              {params.value}
            </Link>
          </Tooltip>
        );
      },
    },
    {
      field: 'version',
      headerName: 'Version',
    },
    {
      field: 'cadenceActionCount',
      headerName: 'Actions',
    },
    {
      field: 'cadenceDispositionRuleCount',
      headerName: 'Call Outcome Rules',
    },
    {
      field: 'cadenceProcessCount',
      headerName: 'Invocations',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params: GridRenderCellParams) => {
        const reportRow = params.row as ViewCadenceReport;
        return (
          <MouseOverPopover
            title={params.value}
            complete={reportRow.cadenceProcessCompletedCount}
            incomplete={reportRow.cadenceProcessIncompleteCount}
            processing={reportRow.cadenceProcessProcessingCount}
            scheduled={undefined}
          />
        );
      },
    },
    {
      field: 'cadenceActionProccessCount',
      headerName: 'Tasks Run',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params: GridRenderCellParams) => {
        const reportRow = params.row as ViewCadenceReport;
        return (
          <MouseOverPopover
            title={params.value}
            complete={reportRow.cadenceActionProcessCompletedCount}
            incomplete={reportRow.cadenceActionProcessIncompleteCount}
            scheduled={reportRow.cadenceActionProcessScheduledCount}
            processing={reportRow.cadenceActionProcessProcessingCount}
          />
        );
      },
    },
    {
      field: 'cadenceProcessMaxCreatedDate',
      headerName: 'Last Triggered',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        typeof params.value === 'string' ||
        typeof params.value === 'number' ||
        params.value instanceof Date
          ? moment(params.value)?.format('MM/DD/YY')
          : '',
    },
    {
      field: 'cadenceActionProcessMaxCreatedDate',
      headerName: 'Last Action Ran',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        typeof params.value === 'string' ||
        typeof params.value === 'number' ||
        params.value instanceof Date
          ? moment(params.value)?.format('MM/DD/YY')
          : '',
    },
    {
      field: '_actions',
      headerName: 'Actions',
      width: 150,
      headerAlign: 'center',
      align: 'center',
      enableSorting: false,
      disableExport: true,
      disableSearch: true, // do not search field content
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      hide: canManage === false, // must be explicit compare else warning: React has detected a change in the order of Hooks called by null
      renderCell: ActionColumn({
        deleteItemId,
        setDeleteItemId,
        isLoading: mutation.isLoading,
        handleDeletion: handlePreflightCheck,
        handleScheduledDeletion: handleScheduledDeletionDialog,
        editLink: '/manage/cadences/:id/edit',
        deleteLink: '/manage/cadences/:id/delete',
        deleteScheduledLink: '/manage/cadences/:id/deleteScheduled',
      }),
    },
  ] as IColDef[];

  const [sortModel, setGridSortModel] = React.useState<GridSortModel>([
    { field: 'cadenceName', sort: 'asc' },
  ]);
  const getLogo = () => {
    return toAbsoluteUrl('/media/logos/cadenceView.svg');
  };
  return (
    <Paper square={true} elevation={0}>
      <Switch>
        <Route path="/manage/cadences/:id/edit">
          {({ history, match }) => (
            <CadenceModal
              show={match != null}
              id={match?.params.id}
              handleClose={() => {
                history.push('/manage/cadences');
              }}
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            />
          )}
        </Route>
        <Route path="/manage/cadences/:id/delete">
          {({ history, match }) => (
            <DeleteCadenceModal
              handleDeletion={handleDeletion}
              show={match != null}
              deleteId={match?.params.id}
              handleClose={() => {
                history.push('/manage/cadences');
              }}
            />
          )}
        </Route>
        <Route path="/manage/cadences/cadenceScheduledActions/:id/delete">
          {({ history, match }) => (
            <DeleteCadenceScheduledModal
              handleDeletion={handleScheduledDeletion}
              show={match != null}
              cadenceId={match?.params.id}
              handleClose={() => {
                history.push('/manage/cadences');
              }}
            />
          )}
        </Route>
        <Route path="/manage/cadences/add">
          {({ history, match }) => (
            <CadenceModal
              id={null}
              show={match != null}
              handleClose={() => {
                history.push('/manage/cadences');
              }}
              enqueueSnackbarWithStyles={enqueueSnackbarWithStyles as any}
            />
          )}
        </Route>
      </Switch>
      {cadenceReportCache.current.length ||
      cadenceReportLoadable.state === 'loading' ? (
        <DataTable
          // passing JSON object as rows to DataTable causes react error
          // className={classes.dataTable}
          ToolbarComponent={ToolbarComponent}
          rows={cadenceReportCache?.current ?? []}
          loading={cadenceReportLoadable.state !== 'hasValue'}
          columns={columns}
          sortModel={sortModel}
          setGridSortModel={setGridSortModel}
        />
      ) : (
        <Paper
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
          square={true}
          elevation={0}
        >
          <div
            style={{
              flexDirection: 'column',
              textAlign: 'center',
            }}
          >
            <h1 style={{ paddingTop: '35px' }}>
              <b>Well Hey There...</b>
            </h1>
            <h2>Let's add your first Cadence</h2>
            <img style={{ height: '500px' }} alt="logo" src={getLogo()} />
            <br />
            <Button
              className={_classes.button}
              name="ADD NEW CADENCE"
              variant="contained"
              startIcon={<AddIcon />}
              color="primary"
              // onClick={() => console.table(['hello', 'world'])}
              onClick={() => {
                history.push('/manage/cadences/add');
              }}
              disableElevation
              // disabled={disabled}
            >
              ADD NEW CADENCE
            </Button>
            <br />
            <Button
              className={_classes.toolbarButton}
              variant="outlined"
              color="primary"
              endIcon={<HelpOutlineIcon />}
              target="_blank"
              href="https://support.kixie.com/hc/en-us/articles/1260803683709"
              rel="noopener noreferrer"
            >
              LEARN MORE ABOUT CADENCES
            </Button>
          </div>
        </Paper>
      )}
    </Paper>
  );
}
